// src/features/statistics/components/AudienceDemographicsSection.tsx

import { useEffect, useMemo, useState } from 'react';

import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../components/ui/utils';
import { useDateRangeParams } from '../hooks/useDateRangeParams';
import { toTimeframe } from '../hooks/useDemographicsFilter';
import { useAudienceDemographics } from '../hooks/useAudienceDemographics';
import { ActivityHourChart } from './ActivityHourChart';
import { AgeBarChart } from './AgeBarChart';
import { GenderChart } from './GenderChart';
import { LocationList } from './LocationList';

import type { AudienceTimeframe } from '../types/audience';

type Props = {
  accountId: string;
  title: string;
  metric: 'follower_demographics' | 'reached_audience_demographics';
};

const TIMEFRAME_OPTIONS: { value: AudienceTimeframe; label: string }[] = [
  { value: 'last_14_days', label: '14 дней' },
  { value: 'last_90_days', label: '90 дней' },
  { value: 'this_month', label: 'Этот месяц' },
  { value: 'prev_month', label: 'Прош. месяц' },
];

function SectionSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-[200px] rounded-xl" />
        <Skeleton className="h-[200px] rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-[180px] rounded-xl" />
        <Skeleton className="h-[180px] rounded-xl" />
      </div>
      <Skeleton className="h-[120px] rounded-xl" />
    </div>
  );
}

export function AudienceDemographicsSection({ accountId, title, metric }: Props): JSX.Element {
  const [globalDateRange] = useDateRangeParams();
  
  const expectedTimeframe = useMemo(() => toTimeframe(globalDateRange), [globalDateRange]);
  const [timeframe, setTimeframe] = useState<AudienceTimeframe>(expectedTimeframe);

  // Sync internal timeframe when global date range changes
  useEffect(() => {
    setTimeframe(expectedTimeframe);
  }, [expectedTimeframe]);

  const { gender, ageGroups, topCities, topCountries, hourlyActivity, isLoading, error } =
    useAudienceDemographics(accountId, timeframe, metric);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        <div className="flex gap-1">
          {TIMEFRAME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setTimeframe(opt.value)}
              className={cn(
                'px-3 py-1 text-xs rounded-lg transition-colors',
                timeframe === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <SectionSkeleton />
      ) : error ? (
        <div className="rounded-xl border bg-muted/20 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {error.message.includes('Personal') || error.message.includes('Business')
              ? 'Демографические данные доступны только для бизнес-аккаунтов и аккаунтов авторов'
              : 'Не удалось загрузить данные аудитории'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Пол</div>
              <GenderChart gender={gender} />
            </div>
            <div className="rounded-xl border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Возраст</div>
              <AgeBarChart ageGroups={ageGroups} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border bg-card p-4">
              <LocationList title="Топ города" items={topCities} />
            </div>
            <div className="rounded-xl border bg-card p-4">
              <LocationList title="Топ страны" items={topCountries} />
            </div>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Активность по часам
            </div>
            <ActivityHourChart data={hourlyActivity} />
          </div>
        </>
      )}
    </div>
  );
}
